import ElakayImage from '../../../components/images/ElakayImage';
import React, { useEffect, useState } from "react";
import { toImageUrl } from "../../../config";
import { dropshippingText } from "../i18n";
import "./ProviderProductPreview.css";

// Aperçu (Product View) d'un Provider Product dans Edit : même idée que la
// Product View du Seller (galerie + fiche) mais avec les informations SOURCE du
// Provider -- prix Provider, minimum, paliers de gros, stock source,
// dropshipping, fulfillment -- et non une fiche de vente au détail. Tout vient
// des valeurs courantes du formulaire (modifications non enregistrées comprises) ;
// chaque nombre est protégé (jamais NaN / undefined / $undefined).
const num = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};
const money = (value, currency = "USD") => {
  try {
    return num(value).toLocaleString("en-US", { style: "currency", currency: currency || "USD" });
  } catch {
    return `$${num(value).toFixed(2)}`;
  }
};

const Flag = ({ label, on, t }) => (
  <li className="ppv__flag">
    <span>{label}</span>
    <span className={`badge ${on ? "badge-success" : "badge-secondary"}`}>{on ? t("t038") : t("t039")}</span>
  </li>
);

const ProviderProductPreview = ({ infoSource, form, images = [], categoryName = "", filterRows = [] }) => {
  const t = dropshippingText(infoSource, "providerProductWizard");
  const [active, setActive] = useState(0);
  const [broken, setBroken] = useState({});
  const list = (images || []).filter(Boolean);
  useEffect(() => {
    if (active >= list.length) setActive(0);
  }, [list.length, active]);

  const src = list[active];
  const currency = form.currency || "USD";
  const stock = num(form.stock);
  const tiers = (form.quantityPricing || [])
    .filter((tier) => num(tier.minQuantity) > 0)
    .map((tier) => ({
      min: num(tier.minQuantity),
      max: tier.maxQuantity ? num(tier.maxQuantity) : null,
      price: tier.pricingType === "discountPercent" ? null : num(tier.unitPrice),
      discount: tier.pricingType === "discountPercent" ? num(tier.discountPercent) : null,
    }))
    .sort((a, b) => a.min - b.min);
  const countries = String(form.allowedCountries || "").split(",").map((c) => c.trim().toUpperCase()).filter(Boolean);

  const stockBadge =
    stock <= 0 ? (
      <span className="badge badge-danger">{t("t140")}</span>
    ) : stock < 20 ? (
      <span className="badge badge-warning">{t("t155", { n: stock })}</span>
    ) : (
      <span className="badge badge-success">{t("t154")}</span>
    );

  return (
    <div className="ppv">
      <div className="ppv__gallery">
        <div className="ppv__main">
          {src && !broken[src] ? (
            <ElakayImage preset="product-detail" loading="eager" fetchPriority="high" src={toImageUrl(src)} alt={form.name || ""} onError={() => setBroken((b) => ({ ...b, [src]: true }))} />
          ) : (
            <div className="ppv__no-image" aria-hidden="true" />
          )}
        </div>
        {list.length > 1 && (
          <ul className="ppv__thumbs">
            {list.map((url, i) => (
              <li key={url}>
                <button type="button" className={i === active ? "is-active" : ""} onClick={() => setActive(i)} aria-label={`${form.name || ""} ${i + 1}`}>
                  <ElakayImage preset="product-thumb" src={toImageUrl(url)} alt="" loading="lazy" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="ppv__info">
        <h2 className="ppv__title">{form.name || t("t046")}</h2>
        {categoryName && <p className="ppv__category">{t("t158")}: {categoryName}</p>}
        <p className="ppv__price">
          <span className="ppv__price-label">{t("t002")}</span> {money(form.basePrice, currency)}
        </p>
        <p>{stockBadge} <span className="text-muted">{t("t004")}: {stock}</span></p>
        <p className="ppv__desc">{form.description ? form.description : <span className="text-muted">{t("t157")}</span>}</p>

        <h3 className="ppv__h">{t("t077")}</h3>
        <ul className="ppv__list">
          <Flag label={t("t021")} on={Boolean(form.wholesaleEnabled)} t={t} />
        </ul>
        <p className="ppv__row"><span>{t("t020")}</span><strong>{Math.max(1, num(form.minimumOrderQuantity, 1))}</strong></p>

        <h3 className="ppv__h">{t("t064")}</h3>
        {tiers.length === 0 ? (
          <p className="text-muted">{t("t065")}</p>
        ) : (
          <table className="table table-sm ppv__tiers">
            <thead>
              <tr><th>{t("t052")}</th><th>{t("t058")}</th></tr>
            </thead>
            <tbody>
              {tiers.map((tier) => (
                <tr key={tier.min}>
                  <td>{tier.min}{tier.max ? `–${tier.max}` : "+"}</td>
                  <td>{tier.price === null ? `−${tier.discount}%` : money(tier.price, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h3 className="ppv__h">{t("t067")}</h3>
        <ul className="ppv__list">
          <Flag label={t("t069")} on={Boolean(form.dropshippingEnabled)} t={t} />
          <Flag label={t("t070")} on={Boolean(form.paidDropshippingEnabled)} t={t} />
          <Flag label={t("t068")} on={Boolean(form.providerStorageEnabled)} t={t} />
          <Flag label={t("t071")} on={Boolean(form.shippingByProviderEnabled)} t={t} />
          <Flag label={t("t072")} on={Boolean(form.sellerPickupEnabled)} t={t} />
          <Flag label={t("t073")} on={Boolean(form.localDeliveryEnabled)} t={t} />
        </ul>
        <p className="ppv__row"><span>{t("t009")}</span><strong>{countries.length ? countries.join(", ") : t("t074")}</strong></p>

        {filterRows.length > 0 && (
          <>
            <h3 className="ppv__h">{t("t159")}</h3>
            <dl className="ppv__filters">
              {filterRows.map((row) => (
                <div key={row.key}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          </>
        )}
      </div>
    </div>
  );
};

export default ProviderProductPreview;
